import { ChangeDetectorRef, Component, HostListener, OnInit } from '@angular/core';
import { Tenant } from '../../../model/tenant';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { TenantsService } from '../../../services/tenants.service';
import { MessageService } from 'primeng/api';
import { JsonEditorService } from '../../../services/json-editor.service';
import { noSpecialCharacters, noWhitespaces } from '../../../shared/custom-validators';
import { ComponentCanDeactivate } from '../../../common/can-deactivate/component-can-deactivate';
import { AuthGuard} from '../../../common/auth-guard.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { CommonMessagesService } from '../../../services/common-messages.service';
import {Organization} from '../../../model/organization';
import {ApplicationNotification} from '../../../model/application-notification';
import {EditorService} from '../../../services/editor.service';
import {LoggedUser} from '../../../common/logged-user.service';
import {ApplicationNotificationPersistRequest} from '../../../model/requests/application-notification-persist-request';
import {ApplicationNotificationsServiceService} from '../../../services/application-notifications.service';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-notifications-form',
  templateUrl: './notifications-form.component.html',
  styleUrls: ['./notifications-form.component.scss'],
  animations: [
    trigger('grow', [
      transition('void <=> *', []),
      transition('* <=> *', [
        style({height: '{{startHeight}}px', opacity: 0}),
        animate('.5s ease'),
      ], {params: {startHeight: 0}})
    ])
  ]
})
export class NotificationsFormComponent implements OnInit, ComponentCanDeactivate {

  public notification: ApplicationNotification;

  public tenants: Tenant[];
  public organizations: Organization[];
  public notificationTypes: any[];
  public protocols: any[];
  public notificationUuid: string;

  public notificationForm: FormGroup;
  public persistingChanges: any;
  public formSuccess: boolean;

  public mode: 'create' | 'edit';
  public editInfo: { tenantUuid: string, organizationUuid: string };
  public appliedToInfoVisible: boolean;

  public emailPattern: RegExp;
  public listUnnotifiedEmails: string[];
  public nodesTemplateInfo: any[];

  constructor(private formBuilder: FormBuilder,
              private route: ActivatedRoute,
              private router: Router,
              private cdr: ChangeDetectorRef,
              public jsonEditorService: JsonEditorService,
              private i18n: TranslocoService,
              private applicationNotificationsServiceService: ApplicationNotificationsServiceService,
              private authGuard: AuthGuard,
              private tenantsService: TenantsService,
              private messageService: MessageService,
              private commonMessagesService: CommonMessagesService,
              public editorService: EditorService,
              private loggedUser: LoggedUser) {

    this.notificationTypes = [
      {name: 'forms.notificationType.ALL', delay: 0},
      {name: 'forms.notificationType.HOURLY', delay: 3600},
      {name: 'forms.notificationType.DAILY', delay: 86400}
    ];

    this.emailPattern = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    this.listUnnotifiedEmails = [];
  }

  ngOnInit(): void {
    this.fillNodesTemplate();
    this.route.url.subscribe(urlSegment => {
      if (urlSegment[0].path === 'new') {
        this.mode = 'create';
        this.tenantsService.getTenantSummaries().toPromise().then(response => {
          this.tenants = response.tenants;
          this.loadCreationForm();
        });
      } else {
        this.mode = 'edit';
        this.tenantsService.getTenantSummaries().toPromise().then(response => {
          this.tenants = response.tenants;
          this.loadEditionForm(urlSegment[0].path, urlSegment[1].path);
        });

      }
    });
  }

  loadCreationForm(): void {
    this.notification = new ApplicationNotification();
    if (!this.authGuard.superAdminRequired()) {
      this.notification.tenant = this.tenants[0];
    }
    this.createForm(this.notification);
  }

  loadEditionForm(tenantUuid, uuid): void {
    this.notificationUuid = uuid;
    this.tenantsService.getTenantApplicationNotification(tenantUuid, uuid).toPromise().then(response => {
      const notification: ApplicationNotificationPersistRequest = response.applicationNotification;
      this.notification = new ApplicationNotification();
      this.editInfo = {tenantUuid: notification.tenantUuid, organizationUuid: notification.organizationUuid};
      this.notification.name = notification.name;
      this.notification.subject = notification.subject;
      this.notification.topic = notification.topic;
      this.notification.template = notification.message;
      this.notification.emails = notification.emails;
      this.notification.tenant = this.tenants.find(x => x.uuid == notification.tenantUuid);
      this.notification.notificationType = notification.frequency === 0 ? this.notificationTypes[0] : notification.frequency === 3600 ? this.notificationTypes[1] : this.notificationTypes[2];
      this.tenantsService.getTenantOrganizationSummaries(notification.tenantUuid).toPromise().then(response => {
        this.organizations = response.organizations;
        this.notification.organization = this.organizations.find(x => x.uuid == notification.organizationUuid);
        this.createForm(this.notification);
      });

    });
  }

  private createForm(notification: ApplicationNotification): void {
    const notificationTypes = this.notificationTypes.filter(st => st.name === notification.notificationType?.name);

    this.notificationForm = this.formBuilder.group({
      name: [notification.name, [Validators.required, Validators.maxLength(255), noSpecialCharacters, noWhitespaces]],
      topic: [notification.topic, [Validators.required, Validators.maxLength(255), noSpecialCharacters, noWhitespaces]],
      subject: [notification.subject, [Validators.required, Validators.maxLength(255), noSpecialCharacters]],
      emails: [notification.emails, [Validators.required]],
      notificationType: [notificationTypes.length > 0 ? notificationTypes[0] : null, [Validators.required, Validators.maxLength(255)]],
      template: [notification.template, [Validators.required]],
      tenant: [notification.tenant, [Validators.required]],
      organization: [notification.organization]
    });
    this.notificationForm.markAsPending();

    if (!this.authGuard.superAdminRequired()) {
      this.notificationForm.get('tenant').disable();
      this.loggedUser.userObservable.subscribe(user => {
        if (user.organization) {
          this.notificationForm.get('organization').disable();
        } else {
          this.loadOrganizations(user.tenantUuid);
        }
      });
    }

    if (this.mode === 'edit') {
      this.notificationForm.get('tenant').disable();
      this.notificationForm.get('organization').disable();
    }

    this.configureEditorsLanguage();
    if (this.mode === 'create') {
      this.addInitialTemplate();
    }

    if ('ROLE_OPERATOR' === this.loggedUser.user.role.toUpperCase()) {
      this.notificationForm.get('name').disable();
      this.notificationForm.get('topic').disable();
      this.notificationForm.get('subject').disable();
      this.notificationForm.get('notificationType').disable();
      this.editorService.editorConfig.editable = false;
      this.editorService.editorConfig.enableToolbar = false;
    }
  }

  private configureEditorsLanguage(): void {
    this.cdr.detectChanges();
  }


  tenantChanged(): void {
    this.notificationForm.get('organization').enable();
    this.notificationForm.get('organization').setValue(null);
    this.tenantsService.getTenantOrganizationSummaries(this.notificationForm.get('tenant').value.uuid).toPromise()
      .then(response => {
        this.organizations = response.organizations;
      });


  }

  persistNotificationData(): void {
    if (!this.notificationForm.valid) {
      return;
    }

    this.persistingChanges = true;
    const persistData: ApplicationNotificationPersistRequest = this.buildPersistData();

    let promise;
    if (this.mode === 'create') {
      promise = this.applicationNotificationsServiceService.createApplicationNotification(persistData).toPromise();
    } else {
      promise = this.applicationNotificationsServiceService.editApplicationNotification(persistData).toPromise();
    }

    promise.then(() => {
      this.formSuccess = true;
      this.router.navigate([`/notifications`]);
    }).catch((error: HttpErrorResponse) => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.name);
      this.persistingChanges = false;
    });
  }

  private buildPersistData(): ApplicationNotificationPersistRequest {
    const formValue = this.notificationForm.getRawValue();
    return {
      uuid: this.mode === 'edit' ? this.notificationUuid : null,
      name: formValue.name,
      topic: formValue.topic,
      frequency: formValue.notificationType.delay,
      emails: formValue.emails,
      tenantUuid: this.mode === 'edit' ? this.editInfo.tenantUuid : formValue.tenant.uuid,
      organizationUuid: this.mode === 'edit' ? this.editInfo.organizationUuid : formValue.organization?.uuid,
      subject : formValue.subject,
      message : formValue.template
    };

  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || this.notificationForm.untouched;
  }

  private loadOrganizations(tenantUuid: string): void {
    this.tenantsService.getTenantOrganizationSummaries(tenantUuid).toPromise()
      .then(response => {
        this.organizations = response.organizations;
      });
  }

  validateEmail(event: { value: string; }): void {
    let listEmails: string[];
    listEmails = this.notificationForm.get('emails').value;
    if (!this.emailPattern.test(event.value)) {
      listEmails.pop();
    } else {
      const index = this.listUnnotifiedEmails.indexOf(event.value, 0);
      if (index >= 0) {
        this.listUnnotifiedEmails.splice(index, 1);
      }
    }
    this.notificationForm.get('emails').setValue(listEmails);
  }

  addUnsubcribedEmails(event): void {
    this.listUnnotifiedEmails.push(event.value);
  }

  addInitialTemplate(): void {
    this.notificationForm.get('template').setValue(`{{#messages}}<b>Número de serie</b>: {{device.serialNumber}}&nbsp;<div><b>Descripción</b>: {{device.description}}<br><b>Cliente</b>: {{device.organization.tenant.name}}&nbsp;</div><div><b>Organización</b>:  {{device.organization.name}}&nbsp;</div><div>{{#device}}</div><div><br></div><div><b>Mediciones del dispositivo:</b></div><div>{{#deviceMeasurements}}</div><div><ul><li>Name: {{name}} , Type: {{measurementType.sensor}}, Unit: {{measurementType.unit}}</li></ul></div><div>{{/deviceMeasurements}}</div><div>{{/device}}</div><div><b>Medidas:</b></div><div>{{#measurements}}</div><div><ul><li>{{name}} : {{value}} ({{timestamp}})</li></ul></div><div>{{/measurements}}</div><div><b>Propiedades:</b></div><div>{{#properties}}</div><div><ul><li>{{name}} : {{value}}</li></ul></div><div>{{/properties}}</div><div><b>Eventos activados:</b></div><div>{{#events}}</div><div><ul><li>&nbsp;{{name}} :  Se ha activado con valor {{evaluatedValue}} a fecha {{triggeredDate}}&nbsp;</li></ul></div><div>{{/events}} </div>{{/messages}}`);
  }

  fillNodesTemplate(): void {
    this.nodesTemplateInfo = [
      {
        key: '0',
        label: 'device',
        children: [
          { key: '0-0', label: 'serialNumber'},
          { key: '0-1', label: 'description'},
          { key: '0-2', label: 'timezone'},
          { key: '0-3', label: 'organization', children: [
              { key: '0-3-0', label: 'name'}, { key: '0-3-1', label: 'tenant', children: [
                  { key: '0-3-1-0', label: 'name'}
                ]}
            ]},
          { key: '0-5', label: 'deviceMeasurements', children: [
              { key: '0-5-0', label: 'name'}, { key: '0-5-1', label: 'measurementType', children: [
                  { key: '0-5-1-0', label: 'sensor'}, { key: '0-5-1-1', label: 'unit'}, { key: '0-5-1-2', label: 'dataType'}
                ]}
            ]},
          { key: '0-6', label: 'assetDevices', children: [
              { key: '0-6-0', label: 'startDate'}, { key: '0-6-1', label: 'asset', children: [
                  { key: '0-6-1-0', label: 'latitude'}, { key: '0-6-1-1', label: 'longitude'}, { key: '0-6-1-2', label: 'timezone'}
                ]}
            ]},
          { key: '0-7', label: 'integrationModules', children: [
              { key: '0-7-0', label: 'integrationIdentifier'}, { key: '0-7-1', label: 'integrationCode'}, { key: '0-7-2', label: 'additionalInfo', children: [
                  { key: '0-7-2-0', label: 'entryPoint'}, { key: '0-7-2-1', label: 'entryProtocol'}, { key: '0-7-2-2', label: 'entryTimestamp'}, { key: '0-7-2-3', label: 'metaInfo'}
                ]}
            ]},
          { key: '0-8', label: 'category', children: [
              { key: '0-8-0', label: 'name'}
            ]},
        ]
      },
      {
        key: '1',
        label: 'measurements',
        children: [
          { key: '1-0', label: 'name'},
          { key: '1-1', label: 'timestamp'},
          { key: '1-2', label: 'unit'},
          { key: '1-3', label: 'value'},
        ]
      },
      {
        key: '2',
        label: 'properties'
      },
      {
        key: '3',
        label: 'events',
        children: [
          { key: '3-0', label: 'name'},
          { key: '3-1', label: 'expression'},
          { key: '3-2', label: 'trigger'},
          { key: '3-3', label: 'evaluatedValue'},
          { key: '3-4', label: 'evaluation'},
          { key: '3-5', label: 'evaluationDate'},
          { key: '3-6', label: 'triggerDate'}
        ]
      },
      {
        key: '4',
        label: 'traceId'
      },
      {
        key: '5',
        label: 'complexEvent',
        children: [
          { key: '4-0', label: 'name'},
          { key: '4-1', label: 'nameComplexRule'},
          { key: '4-2', label: 'evaluatedValue'},
          { key: '4-3', label: 'evaluation'},
          { key: '4-4', label: 'evaluationDate'},
          { key: '4-5', label: 'triggerDate'}
        ]
      }
    ];
  }
}

